import { useCallback, useLayoutEffect, useSyncExternalStore } from "react";

// Whether the desktop navigation sidebar shows labels next to icons. The
// choice persists across sessions; a first-time visitor gets the labeled
// sidebar because icons alone do not explain where to create an instance.
const STORAGE_KEY = "console:sidebarExpanded";
const DATA_ATTRIBUTE = "data-sidebar-expanded";

const listeners = new Set<() => void>();

function readStored(): boolean {
	try {
		const saved = localStorage.getItem(STORAGE_KEY);
		return saved === null ? true : saved === "true";
	} catch {
		return true;
	}
}

let expanded = readStored();

function subscribe(listener: () => void) {
	listeners.add(listener);
	return () => {
		listeners.delete(listener);
	};
}

function getSnapshot() {
	return expanded;
}

function setExpanded(next: boolean) {
	expanded = next;
	try {
		localStorage.setItem(STORAGE_KEY, String(next));
	} catch {
		// Preference stays in memory for this tab.
	}
	for (const listener of listeners) listener();
}

// Pages clear the fixed sidebar with `sm:pl-(--sidebar-width)`; the variable
// is resolved from this attribute in global.css so every page follows the
// toggle without holding its own state.
export function useSidebarExpanded(): [boolean, () => void] {
	const value = useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
	useLayoutEffect(() => {
		document.documentElement.setAttribute(DATA_ATTRIBUTE, String(value));
	}, [value]);
	const toggle = useCallback(() => setExpanded(!getSnapshot()), []);
	return [value, toggle];
}
